import { SignOutButton, useUser } from "@clerk/clerk-react";
import { useApi } from "../lib/useApi";
import { useNotify } from "../notify/useNotify";
import { useCheckConnectionMutation } from "../data";
import { useQueryClient } from "@tanstack/react-query";
import { consoleKeys } from "../data/keys";

interface TopBarProps {
  envLabel: string;
  envKind: "prod" | "dev" | "other";
  lastUpdated: string | null;
}

function fmtClock(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "—";
  const hh = String(t.getHours()).padStart(2, "0");
  const mm = String(t.getMinutes()).padStart(2, "0");
  const ss = String(t.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

export function TopBar({ envLabel, envKind, lastUpdated }: TopBarProps) {
  const { user } = useUser();
  const api = useApi();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const checkConnection = useCheckConnectionMutation(api);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: consoleKeys.status() });
    queryClient.invalidateQueries({ queryKey: consoleKeys.ghlStatus() });
    queryClient.invalidateQueries({ queryKey: consoleKeys.sites() });
  };

  const runCheck = () => {
    notify.promise(checkConnection.mutateAsync(), {
      loading: "Running diagnostic…",
      success: (result) =>
        result.ok
          ? "Resolver diagnostic completed."
          : "Resolver diagnostic found issues; review the panel.",
      error: (err) =>
        `Diagnostic failed: ${err instanceof Error ? err.message : String(err)}`,
    });
  };

  return (
    <header className="topbar">
      <div className="title">
        <p className="eyebrow">
          <span className={`env-pill env-${envKind}`}>{envLabel}</span>
          GHL-Lite
        </p>
        <h1>Operator Console</h1>
      </div>
      <div className="topbar-actions">
        <span className="muted-meta" aria-live="polite">
          {lastUpdated ? `Refreshed ${fmtClock(lastUpdated)}` : "Loading…"}
        </span>
        <button type="button" className="ghost" onClick={refresh}>
          Refresh
        </button>
        <button
          type="button"
          onClick={runCheck}
          disabled={checkConnection.isPending}
        >
          {checkConnection.isPending ? "Running…" : "Run resolver diagnostic"}
        </button>
        <SignOutButton>
          <button type="button" className="ghost" title={user?.primaryEmailAddress?.emailAddress ?? undefined}>
            Sign out
          </button>
        </SignOutButton>
      </div>
    </header>
  );
}
